import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Clock, Ban, Check, PhoneCall, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Tabs, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';
import {
  listAppointments, createAppointment, updateAppointment, deleteAppointment,
  cancelAppointment, EMPTY_APPT,
} from './crmApi';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

// Convert ISO ↔ datetime-local string, respecting the user's local timezone.
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function ApptForm({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <Label htmlFor="a-title">Title</Label>
        <Input id="a-title" data-testid="appt-input-title" value={value.title} onChange={set('title')} />
      </div>
      <div>
        <Label htmlFor="a-start">Start</Label>
        <Input id="a-start" type="datetime-local" data-testid="appt-input-start"
               value={isoToLocalInput(value.start)}
               onChange={(e) => onChange({ ...value, start: localInputToIso(e.target.value) })} />
      </div>
      <div>
        <Label htmlFor="a-dur">Duration (min)</Label>
        <Input id="a-dur" type="number" min={5} step={5} data-testid="appt-input-duration"
               value={value.duration_min}
               onChange={(e) => onChange({ ...value, duration_min: Number(e.target.value) || 0 })} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="a-addr">Address</Label>
        <Input id="a-addr" data-testid="appt-input-address" value={value.address || ''} onChange={set('address')} />
      </div>
      <div>
        <Label htmlFor="a-cname">Client name</Label>
        <Input id="a-cname" data-testid="appt-input-clientname" value={value.client_name || ''} onChange={set('client_name')} />
      </div>
      <div>
        <Label htmlFor="a-cphone">Client phone</Label>
        <Input id="a-cphone" data-testid="appt-input-clientphone" value={value.client_phone || ''} onChange={set('client_phone')} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="a-notes">Notes</Label>
        <Textarea id="a-notes" rows={3} data-testid="appt-input-notes" value={value.notes || ''} onChange={set('notes')} />
      </div>
    </div>
  );
}

const EMPTY_MESSAGES = {
  upcoming: "You're wide open — no appointments coming up.",
  past: 'No past appointments yet.',
  canceled: 'No canceled appointments.',
  all: 'No appointments yet.',
};

function AppointmentRow({ appt, onEdit, onDelete, onCancel }) {
  const isCanceled = appt.status === 'canceled';
  return (
    <li data-testid={`appt-row-${appt.id}`}
        className={`bg-white rounded-xl p-4 shadow-sm flex items-start justify-between gap-3 ${
          isCanceled ? 'opacity-70' : ''
        }`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-semibold text-brand-green truncate ${
            isCanceled ? 'line-through decoration-red-500/70' : ''
          }`}>
            {appt.title || 'Appointment'}
          </div>
          {isCanceled && (
            <span data-testid={`appt-canceled-pill-${appt.id}`}
                  className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              Canceled
            </span>
          )}
        </div>
        <div className="text-sm text-brand-green/70 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {fmt(appt.start)} · {appt.duration_min} min
        </div>
        {appt.client_name && (
          <div className="text-sm text-brand-green/70">
            {appt.client_name}{appt.client_phone ? ` · ${appt.client_phone}` : ''}
          </div>
        )}
        {appt.address && <div className="text-xs text-brand-green/60 truncate">{appt.address}</div>}
        {appt.notes && <div className="text-xs text-brand-green/60 mt-1 line-clamp-2">{appt.notes}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isCanceled && (
          <Button size="sm" variant="ghost" onClick={() => onCancel(appt)}
                  title="Cancel appointment"
                  data-testid={`appt-cancel-${appt.id}`}>
            <Ban className="w-4 h-4 text-yellow-700" />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onEdit(appt)} data-testid={`appt-edit-${appt.id}`}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(appt)} data-testid={`appt-delete-${appt.id}`}>
          <Trash2 className="w-4 h-4 text-red-600" />
        </Button>
      </div>
    </li>
  );
}

function AppointmentsBody({ loading, rows, view, onEdit, onDelete, onCancel }) {
  if (loading) return <p className="text-brand-green/60">Loading…</p>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <p className="text-brand-green/70">{EMPTY_MESSAGES[view] || EMPTY_MESSAGES.all}</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((a) => <AppointmentRow key={a.id} appt={a} onEdit={onEdit}
                                       onDelete={onDelete} onCancel={onCancel} />)}
    </ul>
  );
}

// Pending Confirmations Panel — mirrors the mobile Pending Appointments queue.
// Only visible when at least one row is `pending_confirmation`; polls on mount
// and every time the parent refreshes so newly-arrived AI bookings appear.
function PendingConfirmationsPanel({ onChanged }) {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/appointments/queue/pending');
      setRows(Array.isArray(data) ? data : []);
    } catch (_) { /* non-fatal */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (a) => {
    setBusyId(a.id);
    try {
      const { data } = await api.post(`/appointments/${a.id}/confirm`);
      setRows((prev) => prev.filter((r) => r.id !== a.id));
      const smsErr = data?.sms?.error;
      if (smsErr) {
        toast({
          title: 'Confirmed — but SMS to client failed',
          description: `${smsErr}. Text the client yourself so they don't miss it.`,
          variant: 'destructive',
        });
      } else {
        toast({ title: `Confirmed · ${a.client_name || 'client'} texted` });
      }
      onChanged?.();
    } catch (err) {
      toast({ title: 'Confirm failed', description: err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const reject = async (a) => {
    const reason = window.prompt(
      `Cancel this pending appointment with ${a.client_name || 'the client'}?\n\n` +
      `The client will NOT be texted — you're expected to call them back to reschedule.\n\n` +
      `Optional: reason (visible only to you)`,
      '',
    );
    if (reason === null) return;
    setBusyId(a.id);
    try {
      await api.post(`/appointments/${a.id}/reject`, { reason: reason || undefined });
      setRows((prev) => prev.filter((r) => r.id !== a.id));
      toast({ title: 'Canceled · call the client back to reschedule' });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  if (loading || rows.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3"
      data-testid="pending-appointments-panel"
    >
      <div className="flex items-center gap-2 text-amber-900">
        <AlertTriangle className="w-5 h-5" />
        <div className="font-semibold">
          {rows.length} pending confirmation{rows.length === 1 ? '' : 's'}
        </div>
        <span className="text-xs text-amber-900/70">
          The AI found a clear slot with the caller — tap Confirm to lock it in or Reschedule to cancel and call them back.
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((a) => {
          const isBusy = busyId === a.id;
          const dt = a.start ? new Date(a.start) : null;
          return (
            <div
              key={a.id}
              data-testid={`pending-appt-row-${a.id}`}
              className="bg-white rounded-lg p-3 flex flex-col md:flex-row md:items-center gap-3 border border-amber-200"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <div className="font-semibold text-brand-green truncate">
                    {a.title || 'Showing'}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                    Pending your confirmation
                  </span>
                </div>
                <div className="text-sm text-brand-green/70 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  {dt ? `${dt.toLocaleDateString()} · ${dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : a.start}
                  {' · '}{a.duration_min || 30} min
                </div>
                {a.client_name && (
                  <div className="text-sm text-brand-green/70">
                    {a.client_name}{a.client_phone ? ` · ${a.client_phone}` : ''}
                  </div>
                )}
                {a.address && <div className="text-xs text-brand-green/60 truncate">{a.address}</div>}
                {a.notes && <div className="text-xs text-brand-green/60 mt-1 line-clamp-2">{a.notes}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={() => confirm(a)}
                  disabled={isBusy}
                  data-testid={`pending-appt-confirm-${a.id}`}
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90"
                >
                  {isBusy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> …</> : <><Check className="w-3 h-3 mr-1" /> Confirm</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => reject(a)}
                  disabled={isBusy}
                  data-testid={`pending-appt-reject-${a.id}`}
                  className="text-red-700 hover:bg-red-50"
                >
                  <PhoneCall className="w-3 h-3 mr-1" /> Reschedule
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CrmAppointments() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_APPT);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await listAppointments(view)); }
    finally { setLoading(false); }
  }, [view]);
  useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    setEditing(null);
    setForm({ ...EMPTY_APPT, start: now.toISOString() });
    setShowForm(true);
  };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY_APPT, ...a }); setShowForm(true); };

  const save = async () => {
    if (!form.title || !form.start) {
      toast({ title: 'Title and start time are required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      if (editing) { await updateAppointment(editing.id, form); toast({ title: 'Appointment updated' }); }
      else { await createAppointment(form); toast({ title: 'Appointment created' }); }
      setShowForm(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await deleteAppointment(a.id); toast({ title: 'Appointment deleted' }); await refresh(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  const cancel = async (a) => {
    if (!window.confirm(`Cancel "${a.title}"? The showing stays in history but is removed from your schedule and Google Calendar.`)) return;
    try {
      await cancelAppointment(a.id);
      toast({ title: 'Appointment canceled' });
      await refresh();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="crm-appointments">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-brand-green">Appointments</h2>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="appt-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New appointment
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="appt-form-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit appointment' : 'New appointment'}</DialogTitle>
              <DialogDescription>
                A showing, listing appointment or client meeting. Times use your local timezone.
              </DialogDescription>
            </DialogHeader>
            <ApptForm value={form} onChange={setForm} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="appt-save-btn">
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <PendingConfirmationsPanel onChanged={refresh} />

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList data-testid="appt-tabs">
          <TabsTrigger value="upcoming" data-testid="appt-tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="past" data-testid="appt-tab-past">Past</TabsTrigger>
          <TabsTrigger value="canceled" data-testid="appt-tab-canceled">Canceled</TabsTrigger>
          <TabsTrigger value="all" data-testid="appt-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <AppointmentsBody loading={loading} rows={rows} view={view}
                        onEdit={openEdit} onDelete={remove} onCancel={cancel} />
    </div>
  );
}
